/*
  Site header: menu drawer, its submenu panels, the country/language selects,
  the switch from the translucent overlay state to solid white part way
  down the first section, and on desktop the menu panels and the scrolled
  strip.

  The drawer is positioned from the live bottom edge of the header bar rather
  than a hardcoded offset, because the header is sticky and its distance from
  the top of the viewport changes once the topbar has scrolled away.
*/
if (!customElements.get('site-header')) {
  const TRANSITION_MS = 350;

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.drawer = this.querySelector('[data-menu-drawer]');
      this.bar = this.querySelector('[data-header-bar]');

      this.isOpen = false;

      /*
        The panels showing, outermost first, each with what opened it, so
        Back and Escape go up one level and focus returns to that row. A
        category's panel is one deep; an item inside it opens a second.
      */
      this.subStack = [];

      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);

      if (this.toggle && this.drawer) {
        this.toggle.addEventListener('click', () => {
          this.isOpen ? this.close() : this.show();
        });

        this.drawer.addEventListener('click', this.onDrawerClick.bind(this));

        document.addEventListener('keydown', this.onKeydown);
      }

      /*
        The panels ship with the hidden attribute and it is taken off here,
        once, on load. From now on they are held off screen by a transform
        and taken out of reach with visibility, which is what makes the
        slide smooth: an element coming back from display none has no
        previous position, so the browser has nothing to animate from and
        the first frame lands wherever the final one is.

        The attribute is worth shipping anyway. Without this script the
        panels cannot be opened, so they should not be reachable by tab or
        read out by a screen reader.

        The footer uses this element for its selects only, with no drawer.
      */
      if (this.drawer) {
        this.drawer.querySelectorAll('[data-submenu]').forEach((panel) => {
          panel.removeAttribute('hidden');
        });
      }

      // Country and language pickers are plain forms; submit them on change.
      this.querySelectorAll('[data-localization-select]').forEach((select) => {
        select.addEventListener('change', () => {
          if (select.form) select.form.submit();
        });
      });

      this.setupOverlayState();
      this.setupNav();
      this.setupPinned();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('scroll', this.onPinScroll);
      window.removeEventListener('resize', this.onPinScroll);
      clearTimeout(this.closeTimer);
      clearTimeout(this.navTimer);
      document.body.classList.remove('menu-open');
    }

    /* Overlay to solid ---------------------------------------------------- */

    setupOverlayState() {
      if (!this.classList.contains('site-header--overlay')) return;

      this.threshold = (parseFloat(this.dataset.solidAt) || 50) / 100;

      // The section the header sits over is the first one inside main.
      const main = document.getElementById('MainContent');
      this.overlaid = main ? main.firstElementChild : null;

      this.scrollTicking = false;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();
    }

    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;

      requestAnimationFrame(() => {
        const height = this.overlaid ? this.overlaid.offsetHeight : window.innerHeight;
        this.classList.toggle('is-solid', window.scrollY > height * this.threshold);
        this.scrollTicking = false;
      });
    }

    /* Desktop: scrolled strip ---------------------------------------------- */

    /*
      The section sticks at a negative top, so the search row scrolls away
      and the bottom strip stays. Once it is stuck, [pinned] moves the logo
      and the icons into that strip, which is all CSS.
    */
    setupPinned() {
      this.section = this.closest('.site-header-section');
      if (!this.section) return;

      this.desktop = window.matchMedia('(min-width: 990px)');
      this.onPinScroll = this.onPinScroll.bind(this);
      this.pinTicking = false;
      window.addEventListener('scroll', this.onPinScroll, { passive: true });
      window.addEventListener('resize', this.onPinScroll);
      this.onPinScroll();
    }

    onPinScroll() {
      if (this.pinTicking) return;
      this.pinTicking = true;

      requestAnimationFrame(() => {
        this.pinTicking = false;
        const stickAt = parseFloat(getComputedStyle(this.section).top) || 0;
        const pinned = this.desktop.matches && stickAt < 0 && this.section.getBoundingClientRect().top <= stickAt + 1;
        this.toggleAttribute('pinned', pinned);
        if (this.openItem) this.placeScrim();
      });
    }

    /* Desktop: menu panels ------------------------------------------------- */

    /*
      A panel opens on hover after a moment, so passing over the menu on the
      way somewhere else opens nothing, and closes a little after the
      pointer leaves, so a slightly wide path from the link down into the
      panel does not lose it. Moving straight from one category to another
      swaps the panels at once.

      On touch, the first tap on a category opens its panel and a second
      follows the link. On a keyboard, the down arrow or space opens it and
      moves into the panel; Escape or tabbing out closes it.
    */
    setupNav() {
      this.nav = this.querySelector('[data-nav]');
      this.scrim = this.querySelector('[data-nav-scrim]');
      if (!this.nav) return;

      this.openItem = null;
      this.lastPointer = 'mouse';

      this.nav.querySelectorAll('[data-nav-item]').forEach((item) => {
        const trigger = item.querySelector('[data-nav-trigger]');

        item.addEventListener('mouseenter', () => this.queueNav(item, this.openItem ? 0 : 80));
        item.addEventListener('mouseleave', () => this.queueNav(null, 160));

        trigger.addEventListener('pointerdown', (event) => {
          this.lastPointer = event.pointerType;
        });

        trigger.addEventListener('click', (event) => {
          if (this.lastPointer === 'mouse' || this.openItem === item) return;
          event.preventDefault();
          this.openNav(item);
        });

        trigger.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowDown' && event.key !== ' ') return;
          event.preventDefault();
          this.openNav(item);
          this.focusQuietly(item.querySelector('[data-nav-panel] a'));
        });

        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget) && this.openItem === item) this.closeNav();
        });
      });

      if (this.scrim) {
        this.scrim.addEventListener('click', () => this.closeNav());
        this.scrim.addEventListener('mouseenter', () => this.queueNav(null, 0));
      }

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.openItem) return;
        const trigger = this.openItem.querySelector('[data-nav-trigger]');
        this.closeNav();
        this.focusQuietly(trigger);
      });
    }

    queueNav(item, delay) {
      clearTimeout(this.navTimer);
      this.navTimer = setTimeout(() => (item ? this.openNav(item) : this.closeNav()), delay);
    }

    placeScrim() {
      const bottom = Math.max(this.bar.getBoundingClientRect().bottom, 0);
      this.style.setProperty('--nav-top', `${bottom}px`);
    }

    openNav(item) {
      clearTimeout(this.navTimer);
      if (this.openItem === item) return;

      const switching = Boolean(this.openItem);
      if (switching) {
        this.setAttribute('nav-switching', '');
        this.closeNav(true);
      }

      this.placeScrim();
      item.classList.add('is-open');
      item.querySelector('[data-nav-trigger]').setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.setAttribute('nav-open', '');

      if (switching) requestAnimationFrame(() => this.removeAttribute('nav-switching'));
    }

    closeNav(keepScrim) {
      clearTimeout(this.navTimer);
      if (!this.openItem) return;

      this.openItem.classList.remove('is-open');
      this.openItem.querySelector('[data-nav-trigger]').setAttribute('aria-expanded', 'false');
      this.openItem = null;
      if (!keepScrim) this.removeAttribute('nav-open');
    }

    /* Focus ---------------------------------------------------------------- */

    /*
      Every focus call in this file goes through here, and preventScroll is
      the whole reason it exists.

      Focusing an element normally scrolls it into view. The submenu panels
      sit a full drawer width to the right while they wait, inside a
      container with overflow hidden, so the only way the browser can bring
      one into view is to jump the container sideways. That happens on the
      first frame, cancels the slide, and reads as a flash followed by the
      panel snapping into place.

      preventScroll leaves the scroll position alone and lets the transition
      do the moving. Supported everywhere that matters, and harmless where
      it is not: the option is ignored and focus still lands.
    */
    focusQuietly(element) {
      if (!element) return;

      try {
        element.focus({ preventScroll: true });
      } catch (error) {
        element.focus();
      }
    }

    /* Clicks -------------------------------------------------------------- */

    onDrawerClick(event) {
      if (event.target.hasAttribute('data-menu-overlay')) {
        this.close();
        return;
      }

      const opener = event.target.closest('[data-submenu-open]');
      if (opener) {
        this.showSub(opener.dataset.submenuOpen, opener);
        return;
      }

      if (event.target.closest('[data-submenu-close]')) this.hideSub();
    }

    /* Submenus ------------------------------------------------------------ */

    /*
      Opening is one class on the panel and one attribute on the header, or
      on the panel underneath when it opens over another. Either is what
      moves the list underneath: the two movements are described in CSS and
      start on the same frame, which is the only way they stay in step.
    */
    showSub(index, trigger) {
      const panel = this.drawer.querySelector(`[data-submenu="${index}"]`);
      if (!panel) return;

      const top = this.subStack[this.subStack.length - 1];
      const parentId = panel.dataset.submenuParent;
      const opensOverTop = top && parentId && top.panel.dataset.submenu === parentId;

      // Anything else open goes without animating, so panels never cross.
      if (!opensOverTop) this.hideAllSubs();

      // A panel opened, scrolled through and closed keeps its scroll
      // position, so it would slide back in part way down its own list.
      panel.scrollTop = 0;

      if (opensOverTop) top.panel.classList.add('is-covered');
      panel.classList.add('is-open');
      this.setAttribute('menu-sub-open', '');
      this.subStack.push({ panel, trigger: trigger || null });

      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Focus lands on Back, which is both the first control in the panel
      // and the way out of it.
      this.focusQuietly(panel.querySelector('[data-submenu-close]'));
    }

    // Up one level: the top panel slides away and the one under it returns.
    hideSub(silent) {
      const entry = this.subStack.pop();
      if (!entry) return;

      if (entry.trigger) entry.trigger.setAttribute('aria-expanded', 'false');
      entry.panel.classList.remove('is-open');

      const below = this.subStack[this.subStack.length - 1];
      if (below) below.panel.classList.remove('is-covered');
      else this.removeAttribute('menu-sub-open');

      /*
        silent is for the drawer closing around it, and for one panel
        replacing another. Moving focus then would pull it somewhere the
        shopper is not looking.
      */
      if (!silent) this.focusQuietly(entry.trigger);
    }

    hideAllSubs() {
      while (this.subStack.length) this.hideSub(true);
    }

    /* Drawer -------------------------------------------------------------- */

    position() {
      if (!this.bar || !this.drawer) return;
      const bottom = Math.max(this.bar.getBoundingClientRect().bottom, 0);
      this.drawer.style.setProperty('--menu-top', `${bottom}px`);
    }

    show() {
      clearTimeout(this.closeTimer);
      this.removeAttribute('menu-closing');

      this.position();
      this.isOpen = true;
      this.setAttribute('menu-open', '');
      this.toggle.setAttribute('aria-expanded', 'true');

      // Locking the page also keeps the drawer's top offset valid while open.
      document.body.classList.add('menu-open');

      // Same reason as the panels: the drawer starts off to the left, so
      // focusing into it normally would drag the page across to reach it.
      this.focusQuietly(this.drawer.querySelector('a, button'));
    }

    close() {
      /*
        Any open panel is dropped as the drawer goes, so the menu is back at
        its top level next time it opens. Leaving it open would mean the
        drawer reopens showing a category list with no explanation of how it
        got there.
      */
      this.hideAllSubs();

      this.isOpen = false;
      this.removeAttribute('menu-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      this.focusQuietly(this.toggle);

      // Keeps the drawer rendered while the scrim fades and the panel slides
      // out. Without this it would be hidden on the same frame and the close
      // would read as instant.
      this.setAttribute('menu-closing', '');
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        this.removeAttribute('menu-closing');
      }, TRANSITION_MS);
    }

    /*
      Escape backs out one level at a time: out of each panel first, then
      out of the menu. Closing everything at once would lose the shopper's place
      for what is usually a mis-hit key.
    */
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;

      if (this.subStack.length) {
        this.hideSub();
        return;
      }

      this.close();
    }
  }

  customElements.define('site-header', SiteHeader);
}
